import { useState, useEffect, useRef } from 'react'
import { useAI, MAX_MESSAGE_LENGTH } from '../stores/AIContext'
import { useI18n } from '../stores/I18nContext'
import AIMessage from './AIMessage'
import {
  TEXT_ACCEPT, MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES,
  readTextAttachment, composeMessage,
} from '../utils/attachments'
import { downloadUrl } from '../utils/download'
import './AIAssistantPanel.css'

const SKILL_URL = '/skills/time-distance-plan.skill'

// Save the skill as a Blob so a PWA service worker can't swap in index.html.
function handleSkillDownload(e) {
  e.preventDefault()
  downloadUrl(SKILL_URL, 'time-distance-plan.skill').catch(() => {
    // Fall back to a normal navigation if the fetch-based save fails.
    window.location.href = SKILL_URL
  })
}

export default function AIAssistantPanel() {
  const {
    isOpen, togglePanel, messages, isStreaming, error, sendMessage, clearMessages, cancelRequest,
    applySuggestion, dismissSuggestion, sessions, currentSessionId, createSession, switchSession, archiveSession, unarchiveSession, summarizeAndPinSession, archivedSessions, removeSession,
    availableModels, currentModel, setModel,
    teddoMemory, addPinnedNote, removePinnedNote, allowDismissedSuggestion, clearTeddoMemory
  } = useAI()
  const { language } = useI18n()
  const [input, setInput] = useState('')
  const [attachments, setAttachments] = useState([])
  const [attachError, setAttachError] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [memoryOpen, setMemoryOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [showAccepted, setShowAccepted] = useState(false)
  const [showDismissed, setShowDismissed] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  const isNorwegian = language === 'nb'

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Effective payload = typed text + any attached files folded in. The length
  // checks and char counter track this composed length, not just the typed
  // text, so attachments count toward the limit.
  const composed = composeMessage(input, attachments)
  const composedLength = composed.length
  const overLimit = composedLength > MAX_MESSAGE_LENGTH
  const nearLimit = composedLength > MAX_MESSAGE_LENGTH * 0.8
  const hasContent = input.trim().length > 0 || attachments.length > 0

  // Read dropped/picked files into text attachments, with friendly,
  // localized errors for unsupported or oversized files.
  const addFiles = async (fileList) => {
    setAttachError('')
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    const room = MAX_ATTACHMENTS - attachments.length
    if (room <= 0) {
      setAttachError(isNorwegian
        ? `Du kan legge ved opptil ${MAX_ATTACHMENTS} filer.`
        : `You can attach up to ${MAX_ATTACHMENTS} files.`)
      return
    }
    const errors = []
    const added = []
    for (const file of files.slice(0, room)) {
      try {
        added.push(await readTextAttachment(file))
      } catch (err) {
        const kb = Math.round(MAX_ATTACHMENT_BYTES / 1024)
        if (err.message === 'too-large') {
          errors.push(isNorwegian
            ? `"${file.name}" er for stor (maks ${kb} KB).`
            : `"${file.name}" is too large (max ${kb} KB).`)
        } else if (err.message === 'unsupported-type') {
          errors.push(isNorwegian
            ? `"${file.name}" støttes ikke. Bruk tekstfiler som JSON, Markdown eller .txt.`
            : `"${file.name}" isn't supported. Use text files like JSON, Markdown, or .txt.`)
        } else {
          errors.push(isNorwegian
            ? `Klarte ikke å lese "${file.name}".`
            : `Could not read "${file.name}".`)
        }
      }
    }
    if (files.length > room) {
      errors.push(isNorwegian
        ? `Bare de første ${room} filene ble lagt til (maks ${MAX_ATTACHMENTS}).`
        : `Only the first ${room} file(s) were added (max ${MAX_ATTACHMENTS}).`)
    }
    if (added.length > 0) setAttachments(prev => [...prev, ...added])
    if (errors.length > 0) setAttachError(errors.join(' '))
  }

  const handleFileInput = (e) => {
    addFiles(e.target.files)
    e.target.value = '' // allow re-picking the same file
  }

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
    setAttachError('')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!hasContent || isStreaming) return

    const message = composeMessage(input, attachments)
    if (message.length > MAX_MESSAGE_LENGTH) {
      // Let sendMessage surface the friendly, localized warning, but keep the
      // text and attachments so the user can trim instead of starting over.
      sendMessage(message)
      return
    }

    sendMessage(message)
    setInput('')
    setAttachments([])
    setAttachError('')
  }

  const handleKeyDown = (e) => {
    // Submit on Enter (but not Shift+Enter for multi-line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const suggestedQuestions = [
    {
      en: "What's the total estimated time for this project?",
      nb: "Hva er totalt estimert tid for dette prosjektet?"
    },
    {
      en: "Suggest improvements to this plan",
      nb: "Foreslå forbedringer til denne planen"
    },
    {
      en: "Which tasks are on the critical path?",
      nb: "Hvilke oppgaver er på kritisk sti?"
    },
    {
      en: "Help me break down the next checkpoint",
      nb: "Hjelp meg å dele opp neste kontrollpunkt"
    }
  ]

  if (!isOpen) return null

  return (
    <div className="ai-assistant-panel">
      <div className="ai-header">
        <div className="ai-title">
          <img src="/teddo_the_icon_with_transparent.png" alt="Teddo" className="ai-panel-icon" />
          <h2>{isNorwegian ? 'AI-assistent' : 'AI Assistant'}</h2>
        </div>
        <button className="close-button" onClick={togglePanel} title={isNorwegian ? 'Lukk' : 'Close'}>
          ×
        </button>
      </div>

      <div className="ai-body">
        {availableModels.length > 0 && (
          <div className="ai-model-bar">
            <label className="ai-model-label">
              {isNorwegian ? 'Modell' : 'Model'}
            </label>
            <select
              className="ai-model-select"
              value={currentModel || ''}
              onChange={e => setModel(e.target.value)}
              disabled={isStreaming}
              title={isNorwegian ? 'Velg AI-modell' : 'Select AI model'}
            >
              {availableModels.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>
        )}
        {(sessions.length > 0 || (archivedSessions?.length || 0) > 0) && (
          <div className="ai-session-bar">
            <div className="ai-session-current">
              {sessions.find(s => s._id === currentSessionId)?.title || (isNorwegian ? 'Ingen økt' : 'No session')}
            </div>
            <div className="ai-session-actions">
              {(sessions.length + (archivedSessions?.length || 0)) > 0 && (
                <select
                  className="ai-session-select"
                  value={currentSessionId || ''}
                  onChange={e => {
                    const id = e.target.value
                    if (!id) return
                    if (archivedSessions?.some(s => s._id === id)) {
                      unarchiveSession(id)
                    } else {
                      switchSession(id)
                    }
                  }}
                  title={isNorwegian ? 'Bytt økt' : 'Switch session'}
                >
                  {!currentSessionId && (
                    <option value="" disabled>
                      {isNorwegian ? '— velg økt —' : '— select session —'}
                    </option>
                  )}
                  <optgroup label={isNorwegian ? 'Aktive' : 'Active'}>
                    {sessions.map(s => (
                      <option key={s._id} value={s._id}>{s.title}</option>
                    ))}
                  </optgroup>
                  {archivedSessions && archivedSessions.length > 0 && (
                    <optgroup label={isNorwegian ? 'Arkiverte' : 'Archived'}>
                      {archivedSessions.map(s => (
                        <option key={s._id} value={s._id}>
                          {s.title} {isNorwegian ? '(gjenopprett)' : '(restore)'}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
              {currentSessionId && (
                <button
                  className="ai-session-btn"
                  onClick={async () => {
                    const summaryMsg = isNorwegian
                      ? 'Oppsummere chatten og feste hovedpoenget til minnet før arkivering?'
                      : 'Summarize this chat and pin the takeaway to memory before archiving?'
                    const wantSummary = window.confirm(summaryMsg)
                    if (wantSummary) {
                      const pinned = await summarizeAndPinSession(currentSessionId)
                      if (pinned) {
                        // Brief visual confirmation
                        window.alert(
                          (isNorwegian ? 'Festet til minnet: ' : 'Pinned to memory: ') + pinned
                        )
                      } else {
                        window.alert(
                          isNorwegian
                            ? 'Ingen varige poenger funnet å feste.'
                            : 'No durable takeaway found to pin.'
                        )
                      }
                    }
                    archiveSession(currentSessionId)
                  }}
                  title={isNorwegian ? 'Arkiver økt' : 'Archive session'}
                >
                  🗂️
                </button>
              )}
              {currentSessionId && (
                <button
                  className="ai-session-btn"
                  onClick={() => {
                    const confirmMsg = isNorwegian
                      ? 'Slette denne økten permanent?'
                      : 'Permanently delete this session?'
                    if (window.confirm(confirmMsg)) {
                      removeSession(currentSessionId)
                    }
                  }}
                  title={isNorwegian ? 'Slett økt' : 'Delete session'}
                >
                  🗑️
                </button>
              )}
              <button
                className="ai-session-btn"
                onClick={createSession}
                title={isNorwegian ? 'Ny økt' : 'New session'}
              >
                +
              </button>
            </div>
          </div>
        )}
        {teddoMemory && (() => {
          const notes = teddoMemory.pinnedNotes || []
          const accepted = teddoMemory.acceptedSuggestions || []
          const dismissed = teddoMemory.dismissedSuggestions || []
          const total = notes.length + accepted.length + dismissed.length
          const handleAddNote = async () => {
            const text = noteDraft.trim()
            if (!text) return
            try {
              await addPinnedNote(text)
              setNoteDraft('')
            } catch (err) {
              console.warn('addPinnedNote failed:', err)
            }
          }
          return (
            <div className="ai-memory-bar">
              <button
                type="button"
                className="ai-memory-toggle"
                onClick={() => setMemoryOpen(o => !o)}
                title={isNorwegian ? 'Teddos minne for dette prosjektet' : "Teddo's memory for this project"}
              >
                <span>{memoryOpen ? '▾' : '▸'} 🧠 {isNorwegian ? 'Minne' : 'Memory'}</span>
                <span className="ai-memory-count">{total}</span>
              </button>
              {memoryOpen && (
                <div className="ai-memory-panel">
                  <div className="ai-memory-section">
                    <div className="ai-memory-section-title">
                      {isNorwegian ? 'Festede notater' : 'Pinned notes'} ({notes.length})
                    </div>
                    {notes.length === 0 && (
                      <div className="ai-memory-empty">
                        {isNorwegian ? 'Ingen notater enda.' : 'No notes yet.'}
                      </div>
                    )}
                    {notes.map(n => (
                      <div key={n.id} className="ai-memory-item">
                        <span className="ai-memory-item-text">{n.text}</span>
                        <button
                          type="button"
                          className="ai-memory-item-btn"
                          onClick={() => removePinnedNote(n.id)}
                          title={isNorwegian ? 'Slett notat' : 'Delete note'}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <div className="ai-memory-add">
                      <textarea
                        className="ai-memory-add-input"
                        rows={2}
                        maxLength={2000}
                        value={noteDraft}
                        onChange={e => setNoteDraft(e.target.value)}
                        placeholder={isNorwegian
                          ? 'Legg til notat Teddo bør huske…'
                          : 'Add a note for Teddo to remember…'}
                      />
                      <button
                        type="button"
                        className="ai-memory-add-btn"
                        onClick={handleAddNote}
                        disabled={!noteDraft.trim()}
                      >
                        {isNorwegian ? 'Lagre' : 'Add'}
                      </button>
                    </div>
                  </div>

                  <div className="ai-memory-section">
                    <button
                      type="button"
                      className="ai-memory-sub-toggle"
                      onClick={() => setShowAccepted(s => !s)}
                    >
                      {showAccepted ? '▾' : '▸'} {isNorwegian ? 'Aksepterte forslag' : 'Accepted suggestions'} ({accepted.length})
                    </button>
                    {showAccepted && accepted.length === 0 && (
                      <div className="ai-memory-empty">
                        {isNorwegian ? 'Ingen enda.' : 'None yet.'}
                      </div>
                    )}
                    {showAccepted && accepted.slice().reverse().map(a => (
                      <div key={a.id} className="ai-memory-item">
                        <span className="ai-memory-item-text">
                          <span className="ai-memory-tag">{a.type}</span>{' '}
                          {a.summary}{a.modified ? (isNorwegian ? ' (endret)' : ' (modified)') : ''}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="ai-memory-section">
                    <button
                      type="button"
                      className="ai-memory-sub-toggle"
                      onClick={() => setShowDismissed(s => !s)}
                    >
                      {showDismissed ? '▾' : '▸'} {isNorwegian ? 'Avviste forslag' : 'Dismissed suggestions'} ({dismissed.length})
                    </button>
                    {showDismissed && dismissed.length === 0 && (
                      <div className="ai-memory-empty">
                        {isNorwegian ? 'Ingen enda.' : 'None yet.'}
                      </div>
                    )}
                    {showDismissed && dismissed.slice().reverse().map(d => (
                      <div key={d.id} className="ai-memory-item">
                        <span className="ai-memory-item-text">
                          <span className="ai-memory-tag">{d.type}</span> {d.summary}
                        </span>
                        <button
                          type="button"
                          className="ai-memory-item-btn"
                          onClick={() => allowDismissedSuggestion(d.id)}
                          title={isNorwegian ? 'Tillat at Teddo foreslår dette igjen' : 'Allow Teddo to suggest this again'}
                        >
                          ↺
                        </button>
                      </div>
                    ))}
                  </div>

                  {total > 0 && (
                    <div className="ai-memory-footer">
                      <button
                        type="button"
                        className="ai-memory-clear-btn"
                        onClick={() => {
                          const msg = isNorwegian
                            ? 'Tømme alt minne for dette prosjektet? Dette kan ikke angres.'
                            : "Clear all memory for this project? This can't be undone."
                          if (window.confirm(msg)) clearTeddoMemory()
                        }}
                      >
                        {isNorwegian ? 'Tøm alt minne' : 'Clear all memory'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })()}
        {messages.length === 0 ? (
          <div className="ai-welcome">
            <img src="/teddo_the_icon_with_transparent.png" alt="Teddo" className="welcome-bear-icon" />
            <h3>{isNorwegian ? 'Hei! Jeg er Teddo' : 'Hi! I\'m Teddo'}</h3>
            <p>
              {isNorwegian
                ? 'Jeg er din AI-assistent for Time-Distance planlegging. Spør meg om noe relatert til din plan!'
                : 'I\'m your AI assistant for Time-Distance planning. Ask me anything about your plan!'}
            </p>

            {suggestedQuestions.length > 0 && (
              <div className="suggested-questions">
                <h4>{isNorwegian ? 'Foreslåtte spørsmål:' : 'Suggested questions:'}</h4>
                {suggestedQuestions.map((q, index) => (
                  <button
                    key={index}
                    className="suggestion-btn"
                    onClick={() => setInput(isNorwegian ? q.nb : q.en)}
                  >
                    {isNorwegian ? q.nb : q.en}
                  </button>
                ))}
              </div>
            )}

            <div className="ai-skill-hint">
              <p className="ai-skill-hint-title">
                {isNorwegian ? 'Bygg en plan med et annet AI-verktøy' : 'Build a plan with another AI tool'}
              </p>
              <a
                className="ai-skill-download"
                href={SKILL_URL}
                download="time-distance-plan.skill"
                onClick={handleSkillDownload}
              >
                ⬇︎ {isNorwegian ? 'Last ned plan-ferdigheten' : 'Download the plan skill'}
              </a>
              <p className="ai-skill-hint-sub">
                {isNorwegian
                  ? 'Gi den til Claude eller ChatGPT, lag planen, og legg JSON-filen ved her med 📎.'
                  : 'Give it to Claude or ChatGPT, build the plan, then attach the JSON here with 📎.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="ai-messages">
            {messages.map((message, index) => (
              <AIMessage key={index} message={message} onApply={applySuggestion} onDismiss={dismissSuggestion} onAnswer={sendMessage} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {error && (
          <div className="ai-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="ai-footer">
        {messages.length > 0 && (
          <button
            className="clear-btn"
            onClick={clearMessages}
            disabled={isStreaming}
            title={isNorwegian ? 'Tøm chat' : 'Clear chat'}
          >
            🗑️
          </button>
        )}

        <form
          className={`ai-input-form${isDragging ? ' dragging' : ''}`}
          onSubmit={handleSubmit}
          onDragOver={(e) => { e.preventDefault(); if (!isStreaming) setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {(attachments.length > 0 || attachError) && (
            <div className="ai-attachments">
              {attachments.map((a, i) => (
                <span className="ai-attachment-chip" key={`${a.name}-${i}`} title={a.name}>
                  <span className="chip-icon">📄</span>
                  <span className="chip-name">{a.name}</span>
                  <button
                    type="button"
                    className="chip-remove"
                    onClick={() => removeAttachment(i)}
                    title={isNorwegian ? 'Fjern' : 'Remove'}
                  >
                    ×
                  </button>
                </span>
              ))}
              {attachError && <span className="ai-attach-error">{attachError}</span>}
            </div>
          )}

          <div className="ai-input-row">
            <button
              type="button"
              className="attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming || attachments.length >= MAX_ATTACHMENTS}
              title={isNorwegian
                ? 'Legg ved tekstfil (JSON, Markdown, .txt …)'
                : 'Attach a text file (JSON, Markdown, .txt …)'}
            >
              📎
            </button>
            <textarea
              ref={inputRef}
              className="ai-input"
              placeholder={isNorwegian ? 'Spør Teddo om noe...' : 'Ask Teddo anything...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              rows={1}
            />
            {nearLimit && (
              <span className={`ai-char-count${overLimit ? ' over-limit' : ''}`}>
                {composedLength.toLocaleString(isNorwegian ? 'nb-NO' : 'en-US')}
                {' / '}
                {MAX_MESSAGE_LENGTH.toLocaleString(isNorwegian ? 'nb-NO' : 'en-US')}
              </span>
            )}
            {isStreaming ? (
              <button
                type="button"
                className="send-btn cancel"
                onClick={cancelRequest}
                title={isNorwegian ? 'Stopp' : 'Stop'}
              >
                ⏹
              </button>
            ) : (
              <button
                type="submit"
                className="send-btn"
                disabled={!hasContent || overLimit}
                title={overLimit
                  ? (isNorwegian ? 'Meldingen er for lang' : 'Message is too long')
                  : (isNorwegian ? 'Send' : 'Send')}
              >
                ➤
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TEXT_ACCEPT}
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </form>
      </div>
    </div>
  )
}
